import clsx from 'clsx'
import {
  FC,
  forwardRef,
  InputHTMLAttributes,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react'
import {amountDigits, amountInWords, groupAmount, regroupAmount} from './amountText'
import './amount.css'

type InputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'defaultValue'
> & {
  /** The amount's digits (Latin, Persian or grouped text alike); empty for none. */
  value: string | number | null | undefined
  /** The digits typed, in Latin and without separators («10000000»), or '' when the field was emptied. */
  onValueChange: (digits: string) => void
}

/**
 * A price field (user request 2026-10-16): the digits grouped by three as they are typed, in Persian, Arabic or Latin
 * digits, with the caret kept before the same digit. Its words go under it with {@link AmountWords}, pointed to
 * by aria-describedby.
 */
export const AmountInput = forwardRef<HTMLInputElement, InputProps>(
  ({value, onValueChange, className, ...rest}, ref) => {
    const input = useRef<HTMLInputElement>(null)
    const caret = useRef<number | null>(null)
    useImperativeHandle(ref, () => input.current as HTMLInputElement)
    const text = groupAmount(amountDigits(value).replace(/^0+(?=\d)/, ''))

    useLayoutEffect(() => {
      if (caret.current === null || !input.current || document.activeElement !== input.current)
        return
      input.current.setSelectionRange(caret.current, caret.current)
      caret.current = null
    }, [text])

    return (
      <input
        {...rest}
        ref={input}
        className={clsx(className, 'mp-amount-input')}
        type='text'
        inputMode='numeric'
        dir='ltr'
        autoComplete='off'
        value={text}
        onChange={(event) => {
          const next = regroupAmount(
            event.target.value,
            event.target.selectionStart ?? event.target.value.length
          )
          caret.current = next.caret
          onValueChange(amountDigits(next.text))
        }}
      />
    )
  }
)
AmountInput.displayName = 'AmountInput'

/** «ده میلیون ریال» under a price field; nothing while it is empty. */
export const AmountWords: FC<{
  value: string | number | null | undefined
  id?: string
  unit?: string
  className?: string
}> = ({value, id, unit = 'ریال', className}) => {
  const words = amountInWords(value)
  if (!words) return null
  return (
    <span id={id} className={clsx('mp-amount-words', className)}>
      {words} {unit}
    </span>
  )
}
